import React, { useState, useEffect } from 'react';
import { X, Check, AlertTriangle, Calendar, Users, BookOpen, Clock, Link2, Pencil } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';
import ChampSelect from './ui/ChampSelect';
import ConfirmDialog from './ConfirmDialog';
import { api } from '../services/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import PorteurNotification from "./ui/PorteurNotification";

// Les deux boutons d'un pied de modal ont la meme taille ET la meme largeur.
// DESIGN-SYSTEM.md, section Boutons.
const BOUTON_PIED = 'h-9 min-w-[168px] justify-center gap-2';


/**
 * Ce que la periode de l'annee permet, ou non.
 *
 * ----------------------------------------------------------------------------
 * Le message d'avant etait faux
 * ----------------------------------------------------------------------------
 * « Les dates de l'annee scolaire ne sont pas definies » decrivait une
 * situation impossible : `annees_scolaires.date_debut` et `date_fin` sont
 * NOT NULL en base. Les dates existaient, c'est le navigateur qui ne les avait
 * jamais chargees — `getActive()` court-circuite l'API et rend un objet sans
 * dates. Le vrai obstacle etait ailleurs : l'annee active etait ECOULEE.
 *
 * ----------------------------------------------------------------------------
 * Quatre etats, et un seul parle
 * ----------------------------------------------------------------------------
 *   illisible  l'annee n'a pas pu etre lue — la seule vraie anomalie
 *   avant      la rentree n'a pas eu lieu : preparer l'emploi du temps
 *              maintenant est precisement le bon moment, on ne dit rien
 *   dans       l'annee court, on ne dit rien
 *   ecoulee    la generation creerait des creneaux entierement dans le passe
 *
 * On AVERTIT sans bloquer. Generer une serie sur une annee passee est inutile
 * mais pas illegitime — reconstituer un historique est un cas reel. Un ecran
 * qui refuse sans expliquer, c'est exactement ce qu'on vient de retirer.
 */
function lirePeriode(activeYear) {
  const debut = activeYear?.date_debut ? String(activeYear.date_debut).slice(0, 10) : null;
  const fin = activeYear?.date_fin ? String(activeYear.date_fin).slice(0, 10) : null;

  if (!debut || !fin) return { etat: 'illisible', debut: null, fin: null };

  // Comparaison de chaines 'AAAA-MM-JJ' : lexicographique, donc juste, et
  // aucun objet Date ne traverse un fuseau horaire au passage.
  const aujourdhui = new Date();
  const jour = [
    aujourdhui.getFullYear(),
    String(aujourdhui.getMonth() + 1).padStart(2, '0'),
    String(aujourdhui.getDate()).padStart(2, '0')
  ].join('-');

  const jours = Math.round((new Date(`${fin}T12:00:00`) - new Date(`${debut}T12:00:00`)) / 86400000) + 1;
  const semaines = Math.max(1, Math.ceil(jours / 7));

  let etat = 'dans';
  let joursDepuisLaFin = 0;
  if (jour < debut) {
    etat = 'avant';
  } else if (jour > fin) {
    etat = 'ecoulee';
    joursDepuisLaFin = Math.round((new Date(`${jour}T12:00:00`) - new Date(`${fin}T12:00:00`)) / 86400000);
  }

  return { etat, debut, fin, jours, semaines, joursDepuisLaFin };
}

const joliJour = (iso) => {
  if (!iso) return '—';
  try {
    return format(new Date(`${iso}T12:00:00`), 'dd MMMM yyyy', { locale: fr });
  } catch {
    return iso;
  }
};

const EmploiDuTempsAnnuelModal = ({ isOpen, onClose, activeYear, onSuccess }) => {
  // États principaux
  const [etapeActuelle, setEtapeActuelle] = useState(0);
  const [classeSelectionnee, setClasseSelectionnee] = useState(null);
  const [affectationsScannees, setAffectationsScannees] = useState([]);
  const [scanValide, setScanValide] = useState(false);
  const [creneauxPlaces, setCreneauxPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // On ne ferme pas une fenetre qui tient du travail non enregistre sans
  // demander. Echap et la croix passent par la.
  const [demandeFermeture, setDemandeFermeture] = useState(false);
  
  // Données
  const [sections, setSections] = useState([]);
  const [classesBySection, setClassesBySection] = useState({});

  const [toastInfo, setToastInfo] = useState(null);  // 🆕 État pour le toast

  // Recalcule a chaque rendu : c'est deux soustractions, et une valeur figee
  // deviendrait fausse si l'annee change sous nos pieds.
  const periode = lirePeriode(activeYear);
  



// Valider et créer l'emploi du temps
const handleValiderEmploiDuTemps = async () => {
  console.log('ActiveYear complet:', activeYear);
  console.log('Dates brutes:', { 
    date_debut: activeYear?.date_debut, 
    date_fin: activeYear?.date_fin 
  });

  if (creneauxPlaces.length === 0) {
  setToastInfo({
    message: 'Veuillez placer au moins un créneau',
    type: 'error'
  });
  return;
}

  //  LOG 1 : AVANT LE MAPPING
  console.log('[1] creneauxPlaces BRUT:', creneauxPlaces);
  console.log('[1] Premier créneau détaillé:', {
    affectation_id: creneauxPlaces[0]?.affectation_id,
    matiere_id: creneauxPlaces[0]?.matiere_id,
    enseignant_id: creneauxPlaces[0]?.enseignant_id,
    matiere_nom: creneauxPlaces[0]?.matiere_nom
  });

  setLoading(true);
  try {
    // Préparer les données pour l'API
    const slots = creneauxPlaces.map(creneau => {
      //  LOG 2 : PENDANT LE MAPPING
      console.log('[2] Mapping créneau:', {
        input_affectation_id: creneau.affectation_id,
        input_matiere: creneau.matiere_nom
      });

      return {
        classe_id: classeSelectionnee.id,
        enseignant_id: creneau.enseignant_id,
        matiere_id: creneau.matiere_id,
        affectation_id: creneau.affectation_id, 
        jour_semaine: creneau.jour,
        heure_debut: creneau.heure_debut,
        heure_fin: creneau.heure_fin,
        salle: '',
        annee_scolaire_id: activeYear.id
      };
    });

    //  LOG 3 : APRÈS LE MAPPING
    console.log('[3] Slots APRÈS mapping:', slots);
    console.log('[3] Premier slot affectation_id:', slots[0]?.affectation_id);

    // Récupérer les horaires min/max pour la série
    const heuresDebut = creneauxPlaces.map(c => c.heure_debut).sort();
    const heuresFin = creneauxPlaces.map(c => c.heure_fin).sort();
    // Vérifier que les dates existent
    // La seule anomalie possible : l'annee n'a pas ete lue. Les colonnes sont
    // NOT NULL en base, donc des dates manquantes signifient un echec de
    // lecture, pas une configuration absente. On le dit tel quel.
    if (periode.etat === 'illisible') {
      throw new Error("L'année scolaire n'a pas pu être lue. Rechargez la page ; si le problème persiste, l'année active est peut-être absente.");
    }

    const mutualisations = creneauxPlaces
      .filter(creneau => creneau.est_mutualise && Array.isArray(creneau.classes_mutualisees) && creneau.classes_mutualisees.length > 0)
      .map(creneau => ({
        classe_principale_id: classeSelectionnee.id,
        classes_ids: creneau.classes_mutualisees,
        enseignant_id: creneau.enseignant_id,
        matiere_id: creneau.matiere_id,
        jour_semaine: creneau.jour,
        heure_debut: creneau.heure_debut,
        heure_fin: creneau.heure_fin,
        annee_scolaire_id: activeYear.id
      }));
    const data = {
      type_recurrence: 'annuel',
      // `periode` tient deja ces deux dates, decoupees de la chaine sans
      // jamais construire d'objet Date — c'est ce que dit son propre
      // commentaire quelques lignes plus haut. Le `format(new Date(...))`
      // qui etait ici lisait la valeur dans le fuseau du NAVIGATEUR : a
      // l'ouest de Greenwich, il rendait la veille.
      date_debut: periode.debut,
      date_fin: periode.fin,
      jours_semaine: [...new Set(creneauxPlaces.map(c => c.jour))],
      heure_debut: heuresDebut[0],
      heure_fin: heuresFin[heuresFin.length - 1],
      salle: '',
      annee_scolaire_id: activeYear.id,
      slots,
      mutualisations
    };

    console.log('Envoi des données:', data);

    // Utiliser l'API existante
    const result = await api.timetable.createSeries(data);
    console.log('Réponse API:', result);

   if (result.success) {
  const poses = Number(result.count || 0);
  setCreneauxPlaces([]);
  setToastInfo({
    message: `Emploi du temps créé avec succès ! ${poses} créneau${poses > 1 ? 'x' : ''} créé${poses > 1 ? 's' : ''}.`,
    type: 'success'
  });

  // On laisse le temps de lire avant de fermer.
  setTimeout(() => {
    if (onSuccess) {
      onSuccess();
    } else {
      onClose();
    }
  }, 1500);
} else {
  // LE DEFAUT DE L'INCIDENT csap2 : sans ce `else`, un refus du serveur
  // n'allait nulle part. `handleResponse` RETOURNE les erreurs metier au
  // lieu de les lever (services/api.js:104) : le `catch` ci-dessous ne les
  // voit pas, et ne les verra jamais.
  //
  // `long: true` force le format centre : l'arbitrage automatique bascule a
  // 130 caracteres, et un conflit d'horaire en fait environ 90.
  setToastInfo({
    message: result?.message || "La création a été refusée par le serveur.",
    type: 'error',
    title: "Création impossible",
    long: true
  });
}

  } catch (error) {
  console.error('Erreur création emploi du temps:', error);
  setToastInfo({
    message: `Erreur lors de la création de l'emploi du temps: ${error.message}`,
    type: 'error'
  });
}
  
  finally {
    setLoading(false);
  }
};




// La fenetre se remet a zero en se fermant : elle reste montee chez son
// parent, et sans cela elle se rouvrirait sur l'etape 4 avec les creneaux de
// la fois precedente.
useEffect(() => {
  if (!isOpen) {
    setEtapeActuelle(0);
    setClasseSelectionnee(null);
    setAffectationsScannees([]);
    setScanValide(false);
    setCreneauxPlaces([]);
    setLoading(false);
  }
}, [isOpen]);




  // Le calcul des trimestres a ete retire.
  //
  // Il lisait `activeYear.debut` et `activeYear.fin` quand l'API rend
  // `date_debut` et `date_fin` : la condition n'etait JAMAIS vraie et la
  // grille des trimestres restait vide a chaque ouverture. Et meme remplie,
  // elle ecrivait en dur le 1er octobre, le 31 decembre, le 1er janvier — les
  // memes trimestres fictifs qu'on vient de retirer de la page de parametrage,
  // faux des qu'une ecole va de septembre a juillet.
  //
  // Ce qui compte avant de generer, ce n'est pas un decoupage invente : c'est
  // la periode reellement couverte. `lirePeriode` la donne.

  // Charger les sections et classes
 const chargerSections = async () => {
  try {
    setLoading(true);
    const sectionsResponse = await api.sections.getAll();
    
    if (sectionsResponse.success) {
      setSections(sectionsResponse.data);
      
      // ✅ Charger TOUTES les classes (avec annexes)
      const classesBySecTemp = {};
      
      for (const section of sectionsResponse.data) {
        console.log(`🔍 Chargement classes pour section: ${section.nom}`);
        
        const classesResponse = await api.classes.getBySection(section.id);
        
        if (classesResponse.success) {
          console.log(`✅ ${classesResponse.data.length} classes trouvées:`, 
            classesResponse.data.map(c => `${c.nom} (principale: ${c.est_classe_principale})`));
          
          classesBySecTemp[section.id] = classesResponse.data;
        }
      }
      
      console.log('📦 Toutes les classes chargées:', classesBySecTemp);
      setClassesBySection(classesBySecTemp);
    }
  } catch (err) {
    console.error('❌ Erreur chargement sections:', err);
    setError('Erreur lors du chargement des sections');
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    if (isOpen && activeYear) {
      chargerSections();
    }
  }, [isOpen, activeYear]);

  // JSX evalue ses enfants avant de les rendre : sans ceci, toute la grille
  // serait construite a chaque rendu du calendrier, fenetre fermee comprise.
  if (!isOpen) return null;

  // Les creneaux poses ne sont dans AUCUNE base tant qu'on n'a pas valide.
  const travailEnCours = creneauxPlaces.length > 0;

  const fermerSiPossible = () => {
    if (travailEnCours) {
      setDemandeFermeture(true);
      return;
    }
    onClose();
  };

  // Étapes du processus
  const etapes = [
    { id: 0, label: 'Année scolaire', icon: Calendar },
    { id: 1, label: 'Sélection classe', icon: Users },
    { id: 2, label: 'Scan enseignants', icon: BookOpen },
    { id: 3, label: 'Emploi du temps', icon: Clock }
  ];

  return (
    // ⚠️ LA COQUILLE EST SUR LES MEMES PRIMITIVES que les deux fenetres
    // qu'elle contient, et ce n'est pas un detail de style.
    //
    // Un dialogue MODAL pose `pointer-events: none` sur le <body> et rend son
    // contenu dans un portail, auquel il redonne `pointer-events: auto`. Un
    // calque fait main autour de lui n'est PAS dans ce portail : il devient
    // inerte des que la fenetre interieure s'ouvre, et l'application entiere
    // parait bloquee. C'est ce qui arrivait en deposant une matiere sur un
    // jour (constate en recette le 14/10/2026).
    //
    // Deux dialogues imbriques du MEME mecanisme s'empilent sans se gener.
    <Dialog open={isOpen} onOpenChange={(ouvert) => { if (!ouvert) fermerSiPossible(); }}>
      <DialogContent
        className="flex h-[90vh] w-[95vw] max-w-none flex-col overflow-hidden p-0 gap-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        // ⚠️ Echap. L'ancien calque fait main n'ecoutait pas le clavier ; la
        // primitive, si. Un appui fermait la fenetre, la remise a zero
        // partait, et les creneaux poses disparaissaient sans un mot.
        //
        // Les fenetres interieures gardent le leur : la primitive ne donne
        // Echap qu'a la couche la plus haute.
        onEscapeKeyDown={(e) => {
          if (travailEnCours) {
            e.preventDefault();
            setDemandeFermeture(true);
          }
        }}
      >
      {/* En-tete fixe. La hauteur de la carte est BORNEE : c'est elle qui
          permet a l'etape 4 d'avoir ses deux ascenseurs, un conteneur
          defilant ne tenant qu'a un parent de hauteur connue. */}
      <DialogHeader className="flex-row items-center justify-between gap-4 space-y-0 border-b px-6 py-4 text-left">
        <div>
          <DialogTitle className="truncate text-xl font-bold text-[#246990]">
            Créer l'emploi du temps annuel
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            Une classe, ses enseignants, et la semaine type reportée sur toute l'année.
          </DialogDescription>
        </div>
        <button
          type="button"
          onClick={fermerSiPossible}
          className="shrink-0 rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100"
          aria-label="Fermer"
        >
          <X className="h-5 w-5" />
        </button>
      </DialogHeader>

        {/* Indicateur d'étapes */}
        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="flex justify-between items-center max-w-4xl mx-auto">
            {etapes.map((etape, index) => {
              const Icon = etape.icon;
              const isActive = etapeActuelle === etape.id;
              const isCompleted = etapeActuelle > etape.id;
              
              return (
                <React.Fragment key={etape.id}>
                  <div className="flex flex-col items-center">
                    <div className={`
                      w-12 h-12 rounded-full flex items-center justify-center
                      transition-all
                      ${isActive ? 'bg-[#319190] text-white scale-110' : ''}
                      ${isCompleted ? 'bg-green-600 text-white' : ''}
                      ${!isActive && !isCompleted ? 'bg-gray-200 text-gray-400' : ''}
                    `}>
                      {isCompleted ? <Check className="w-6 h-6" /> : <Icon className="w-6 h-6" />}
                    </div>
                    <span className={`
                      text-xs mt-2 font-medium
                      ${isActive ? 'text-[#319190]' : ''}
                      ${isCompleted ? 'text-green-600' : ''}
                      ${!isActive && !isCompleted ? 'text-gray-400' : ''}
                    `}>
                      {etape.label}
                    </span>
                  </div>
                  
                  {index < etapes.length - 1 && (
                    <div className={`
                      flex-1 h-1 mx-4 rounded
                      ${isCompleted ? 'bg-green-600' : 'bg-gray-200'}
                    `} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

      {/* Corps : la seule zone qui defile */}
      <div className="min-h-0 flex-1 overflow-auto p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              {error}
            </div>
          )}

          {etapeActuelle === 0 && (
            <EtapeAnneeScolaire 
              activeYear={activeYear}
              periode={periode}
              onContinuer={() => setEtapeActuelle(1)}
            />
          )}

          {etapeActuelle === 1 && (
            <EtapeSelectionClasse
                sections={sections}
                classesBySection={classesBySection}
                classeSelectionnee={classeSelectionnee}
                onSelectClasse={(classe) => {
                setClasseSelectionnee(classe);
                console.log('Classe sélectionnée:', classe); // Debug
                }}
                onContinuer={() => {
                if (classeSelectionnee) {
                    setEtapeActuelle(2);
                }
                }}
                onRetour={() => setEtapeActuelle(0)}
            />
            )}

          {etapeActuelle === 2 && (
            <EtapeScanEnseignants
              classeSelectionnee={classeSelectionnee}
              activeYear={activeYear}
              affectationsScannees={affectationsScannees}
              setAffectationsScannees={setAffectationsScannees}
              scanValide={scanValide}
              setScanValide={setScanValide}
              setToastInfo={setToastInfo}
              onContinuer={() => setEtapeActuelle(3)}
              onRetour={() => setEtapeActuelle(1)}
            />
          )}


          {etapeActuelle === 3 && (
            <EtapeEmploiDuTemps
                classeSelectionnee={classeSelectionnee}
                affectationsScannees={affectationsScannees}
                activeYear={activeYear}
                periode={periode}
                creneauxPlaces={creneauxPlaces}
                setCreneauxPlaces={setCreneauxPlaces}
                 setToastInfo={setToastInfo} 
                onRetour={() => setEtapeActuelle(2)}
                onValider={handleValiderEmploiDuTemps}
                enCours={loading}
            />
            )}

      </div>

      {/* PorteurNotification : ToastPortal, et bascule en format long pour
          les messages du serveur. */}
      <PorteurNotification notification={toastInfo} onFermer={() => setToastInfo(null)} />
      </DialogContent>

      <ConfirmDialog
        isOpen={demandeFermeture}
        onClose={() => setDemandeFermeture(false)}
        onConfirm={() => {
          setDemandeFermeture(false);
          onClose();
        }}
        type="warning"
        title="Quitter sans enregistrer ?"
        description={`${creneauxPlaces.length} créneau${creneauxPlaces.length > 1 ? 'x' : ''} `
          + `${creneauxPlaces.length > 1 ? 'ont' : 'a'} été placé${creneauxPlaces.length > 1 ? 's' : ''} `
          + "et n'" + (creneauxPlaces.length > 1 ? 'ont' : 'a') + ' pas encore été enregistré'
          + (creneauxPlaces.length > 1 ? 's' : '') + '. En quittant, ils seront perdus.'}
        confirmText="Quitter sans enregistrer"
        cancelText="Reprendre"
      />
    </Dialog>
  );
};

// Composant Étape 0 : Année scolaire
/**
 * Ce qu'on montre AVANT de generer quoi que ce soit.
 *
 * L'ecran annoncait « Les creneaux seront crees pour toute l'annee » au-dessus
 * d'une grille de trimestres vide, et on decouvrait la periode reellement
 * couverte apres avoir clique. On la donne maintenant en chiffres, et on dit
 * si elle a du sens aujourd'hui.
 */
const EtapeAnneeScolaire = ({ activeYear, periode, onContinuer }) => {
  const ecoulee = periode?.etat === 'ecoulee';
  const illisible = periode?.etat === 'illisible';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Année scolaire {activeYear?.nom || 'non lue'}
        </h3>
        {!illisible && (
          <p className="text-gray-600">
            Du {joliJour(periode.debut)} au {joliJour(periode.fin)}
          </p>
        )}
      </div>

      {illisible ? (
        <div className="rounded-lg border-2 border-red-200 bg-red-50 p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
            <div className="text-sm text-red-900">
              <p className="mb-1 font-semibold">L'année scolaire n'a pas pu être lue</p>
              <p>
                Rechargez la page. Si le problème persiste, c'est qu'aucune année n'est
                active — le parcours de clôture, dans Outils administratifs, en désigne une.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* La periode, en chiffres, avant de cliquer. */}
          <div className="rounded-xl border-2 border-[#319190]/20 bg-gradient-to-br from-[#319190]/10 to-[#63b5b4]/10 p-6">
            <p className="text-sm font-semibold text-[#319190] mb-2">
              Ce que la génération va créer
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              Chaque créneau que vous placerez sera reporté toutes les semaines, du{' '}
              <strong>{joliJour(periode.debut)}</strong> au{' '}
              <strong>{joliJour(periode.fin)}</strong> — soit{' '}
              <strong>{periode.semaines} semaines</strong>.
            </p>
            <p className="mt-2 text-xs text-gray-500">
              Ces deux dates sont les bornes de l'année scolaire. Elles se corrigent dans
              Outils administratifs → Paramétrage du calendrier annuel, et n'ont aucun
              effet sur le calendrier de paie ni sur les salaires.
            </p>
          </div>

          {/* L'annee est derriere nous : on le dit, on ne bloque pas. */}
          {ecoulee && (
            <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                <div className="text-sm text-amber-900">
                  <p className="mb-1 font-semibold">
                    Cette année scolaire est terminée depuis {periode.joursDepuisLaFin} jours
                  </p>
                  <p className="leading-relaxed">
                    Elle s'est achevée le <strong>{joliJour(periode.fin)}</strong>. Les créneaux
                    créés seront donc tous datés dans le passé. Vous pouvez continuer — c'est
                    utile pour reconstituer un historique — mais si vous prépariez la rentrée,
                    voici les deux chemins :
                  </p>
                  <ul className="mt-2 space-y-1 list-disc list-inside">
                    <li>
                      <strong>Clôturer l'année</strong> (Outils administratifs → Clôture de
                      l'année) : l'année suivante devient active, avec ses propres dates.
                    </li>
                    <li>
                      <strong>Repousser la date de fin</strong> (Paramétrage du calendrier
                      annuel), si votre année court encore réellement.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div className="flex justify-end mt-8">
        <button
          onClick={onContinuer}
          disabled={illisible}
          className={`px-6 py-3 rounded-lg font-medium transition-colors ${
            illisible
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-[#319190] text-white hover:bg-[#2a7a79]'
          }`}
        >
          Continuer
        </button>
      </div>
    </div>
  );
};

// Composant Étape 1 : Sélection classe
const EtapeSelectionClasse = ({ 
  sections, 
  classesBySection, 
  classeSelectionnee, 
  onSelectClasse, 
  onContinuer, 
  onRetour 
}) => {
  const [sectionActive, setSectionActive] = useState(null);

  // Auto-sélectionner la première section qui a des classes
  useEffect(() => {
    if (!sectionActive && sections.length > 0) {
      const premiereSectionAvecClasses = sections.find(s => 
        classesBySection[s.id] && classesBySection[s.id].length > 0
      );
      if (premiereSectionAvecClasses) {
        setSectionActive(premiereSectionAvecClasses.id);
      }
    }
  }, [sections, classesBySection, sectionActive]);

  const totalClasses = Object.values(classesBySection).reduce(
    (sum, classes) => sum + (classes?.length || 0), 
    0
  );

  return (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Sélectionnez une classe
        </h3>
        <p className="text-gray-600">
          {totalClasses} classe(s) disponible(s) dans {sections.length} section(s)
        </p>
      </div>

      <div className="grid grid-cols-[250px_1fr] gap-6">
        {/* Liste des sections */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Sections</h4>
          {sections.map((section) => {
            const nbClasses = classesBySection[section.id]?.length || 0;
            const isActive = sectionActive === section.id;
            
            return (
              <button
                key={section.id}
                onClick={() => setSectionActive(section.id)}
                className={`
                  w-full text-left px-4 py-3 rounded-lg transition-all
                  ${isActive 
                    ? 'bg-[#319190] text-white shadow-md' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }
                `}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{section.nom}</span>
                  <span className={`
                    text-xs px-2 py-1 rounded-lg
                    ${isActive ? 'bg-white/20' : 'bg-gray-200'}
                  `}>
                    {nbClasses}
                  </span>
                </div>
              </button>
            );
          })}

          {/* Section sans section */}
          {classesBySection['null']?.length > 0 && (
            <button
              onClick={() => setSectionActive('null')}
              className={`
                w-full text-left px-4 py-3 rounded-lg transition-all
                ${sectionActive === 'null' 
                  ? 'bg-[#319190] text-white shadow-md' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                }
              `}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">Sans section</span>
                <span className={`
                  text-xs px-2 py-1 rounded-lg
                  ${sectionActive === 'null' ? 'bg-white/20' : 'bg-gray-200'}
                `}>
                  {classesBySection['null'].length}
                </span>
              </div>
            </button>
          )}
        </div>

        {/* Liste des classes de la section active */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Classes de la section
          </h4>
          
          {sectionActive && classesBySection[sectionActive] ? (
            <div className="grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-2">
              {classesBySection[sectionActive].map((classe) => {
                const isSelected = classeSelectionnee?.id === classe.id;
                
                return (
                  <div
                    key={classe.id}
                    onClick={() => onSelectClasse(classe)}
                    className={`
                      p-4 rounded-lg border-2 cursor-pointer transition-all
                      hover:shadow-md
                      ${isSelected 
                        ? 'border-[#319190] bg-gradient-to-r from-[#319190]/10 to-[#63b5b4]/10 shadow-md' 
                        : 'border-gray-200 hover:border-[#319190]/30'
                      }
                    `}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h5 className="font-semibold text-gray-900">
                          {classe.nom}
                        </h5>
                        <div className="mt-2 space-y-1">
                          
                          {classe.effectif !== undefined && (
                            <div className="text-xs text-gray-600">
                              Effectif: {classe.effectif} élève(s)
                            </div>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <Check className="w-5 h-5 text-[#319190] flex-shrink-0" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>Aucune classe dans cette section</p>
            </div>
          )}
        </div>
      </div>

      {/* Classe sélectionnée - Info en bas */}
      {classeSelectionnee && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-3">
            <Check className="w-5 h-5 text-blue-600" />
            <div>
              <p className="text-sm font-medium text-blue-900">
                Classe sélectionnée : {classeSelectionnee.nom}
              </p>
              <p className="text-xs text-blue-700 mt-1">
                Taux horaire : {classeSelectionnee.taux_horaire || 0} F/heure
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Boutons navigation */}
      <div className="flex justify-between mt-8">
        <button
          onClick={onRetour}
          className="px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
        >
          Retour
        </button>
        <button
          onClick={onContinuer}
          disabled={!classeSelectionnee}
          className={`
            px-6 py-3 rounded-lg font-medium transition-all
            ${classeSelectionnee
              ? 'bg-[#319190] text-white hover:bg-[#2a7a79]'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }
          `}
        >
          Continuer
        </button>
      </div>
    </div>
  );
};


// Composant Étape 2 : Scan enseignants
const EtapeScanEnseignants = ({ 
  classeSelectionnee, 
  activeYear, 
  affectationsScannees, 
  setAffectationsScannees, 
  scanValide, 
  setScanValide, 
  setToastInfo, 
  onContinuer, 
  onRetour 
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // ⚠️ L'HEURE DU DERNIER SCAN RÉUSSI, ET ELLE N'EST PAS DÉCORATIVE.
  //
  // « Rescanner les affectations » relit bien les trois sources, mais quand rien
  // n'a changé côté affectations il reconstruit un tableau IDENTIQUE : l'écran ne
  // bouge pas d'un pixel, et le bouton passe pour mort. Il l'a été signalé comme
  // tel le 05/10/2026. Cette heure change à chaque relecture réussie, même quand
  // le résultat est le même : c'est la seule preuve que le clic a servi.
  const [dernierScanLe, setDernierScanLe] = useState(null);



  // Scanner les affectations
// Scanner les affectations
const handleScanner = async () => {
  // ⚠️ LE SEUL CHEMIN OÙ CE BOUTON ÉTAIT VRAIMENT MUET.
  //
  // Ce garde-fou sortait sur un simple `console.log` : pas de roue, pas
  // d'erreur, pas d'heure — un clic absolument sans effet visible, et le défaut
  // était dans la console du navigateur, où personne ne regarde. C'est
  // normalement impossible à ce stade (les deux étapes précédentes sont
  // validées), mais « normalement impossible » et « silencieux » font une
  // mauvaise paire : le jour où ça arrive, on cherche ailleurs.
  if (!classeSelectionnee || !activeYear) {
    console.log('Classe ou année manquante:', { classeSelectionnee, activeYear });
    setError(
      !classeSelectionnee
        ? "Aucune classe sélectionnée : revenez à l'étape précédente."
        : "L'année scolaire active n'a pas pu être lue : rechargez la page."
    );
    return;
  }

  console.log('Début du scan pour:', classeSelectionnee.nom);
  setLoading(true);
  setError(null);

  try {
    // 1. Récupérer les détails complets de la classe
    console.log('Appel API getDetailsComplets pour classe_id:', classeSelectionnee.id);
    const detailsResponse = await api.classes.getDetailsComplets(classeSelectionnee.id);
    console.log('Réponse getDetailsComplets:', detailsResponse);
    
    if (!detailsResponse.success) {
      throw new Error('Impossible de récupérer les détails de la classe');
    }

    const { matieres } = detailsResponse.data;
    console.log('Matières trouvées:', matieres);

    // 2. Récupérer les affectations enseignants-matières pour cette classe
    console.log('Appel API affectations.enseignantsMatieres.getAll');
    const affectationsResponse = await api.affectations.enseignantsMatieres.getAll(
      activeYear.id,
      { classe_id: classeSelectionnee.id }
    );
    console.log('Réponse affectations matières:', affectationsResponse);

    if (!affectationsResponse.success) {
      throw new Error('Impossible de récupérer les affectations');
    }

    // 3. Récupérer l'enseignant principal si existe
    console.log('Appel API affectations.enseignantsPrincipaux.getAll');
    const principalResponse = await api.affectations.enseignantsPrincipaux.getAll(activeYear.id);
    console.log('Réponse enseignant principal:', principalResponse);
    
    const enseignantPrincipal = principalResponse.success 
      ? principalResponse.data.find(a => a.classe_id === classeSelectionnee.id)
      : null;
    console.log('Enseignant principal trouvé:', enseignantPrincipal);

    // 4. Créer le tableau de scan
    const affectationsScan = [];

    // Ajouter l'enseignant principal si existe
    if (enseignantPrincipal) {
      affectationsScan.push({
        type: 'principal',
        matiere_nom: 'Enseignant principal',
        matiere_id: null,
        enseignant_id: enseignantPrincipal.enseignant_id,
        enseignant_nom: enseignantPrincipal.enseignant_nom,
        enseignant_prenom: enseignantPrincipal.enseignant_prenom,
        affectation_id: enseignantPrincipal.id,
        statut: 'affecte'
      });
    }

    // Ajouter les matières avec leurs enseignants
    console.log('Traitement des matières...');
    matieres.forEach(matiere => {
      const affectation = affectationsResponse.data.find(
        a => a.matiere_id === matiere.id && a.classe_id === classeSelectionnee.id
      );

      //  DEBUG CRUCIAL ICI
      console.log(`Matière "${matiere.nom}" (ID: ${matiere.id}):`, {
        affectation_trouvee: affectation,
        affectation_id: affectation?.id || null,
        enseignant_id: affectation?.enseignant_id || null,
        enseignant_nom: affectation?.enseignant_nom || null
      });

      affectationsScan.push({
        type: 'matiere',
        matiere_nom: matiere.nom,
        matiere_id: matiere.id,
        enseignant_id: affectation?.enseignant_id || null,
        enseignant_nom: affectation?.enseignant_nom || null,
        enseignant_prenom: affectation?.enseignant_prenom || null,
        affectation_id: affectation?.id || null,
        statut: affectation ? 'affecte' : 'manquant'
      });
    });

    console.log(' Affectations scannées (total):', affectationsScan);
    
    //  DEBUG FINAL - Vérifier tous les affectation_id
    console.log('Récapitulatif des affectation_id:');
    affectationsScan.forEach((aff, index) => {
      console.log(`  [${index}] ${aff.matiere_nom}: affectation_id = ${aff.affectation_id}`);
    });
    
    setAffectationsScannees(affectationsScan);
    // Seulement sur un scan RÉUSSI : une heure qui avance alors que la lecture a
    // échoué dirait le contraire de la vérité. L'échec, lui, s'affiche en rouge
    // au-dessus du tableau.
    setDernierScanLe(new Date());

  } catch (err) {
    console.error('Erreur scan:', err);
    setError(err.message || 'Erreur lors du scan des affectations');
  } finally {
    setLoading(false);
    console.log('Scan terminé');
  }
};




  const affectationsManquantes = affectationsScannees.filter(a => a.statut === 'manquant').length;
  const peutContinuer = affectationsScannees.length > 0 && affectationsManquantes === 0;

  return (
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Scan des enseignants - {classeSelectionnee.nom}
        </h3>
        <p className="text-gray-600">
          Vérrifiez que tous les enseignants sont bien affectés aux matières
        </p>
      </div>

      {/* Bouton Scanner */}
      {affectationsScannees.length === 0 && (
        <div className="mb-6">
          <button
            onClick={handleScanner}
            disabled={loading}
            className="w-full py-4 bg-[#319190] text-white rounded-lg hover:bg-[#2a7a79] transition-colors font-medium flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Scan en cours...
              </>
            ) : (
              <>
                <BookOpen className="w-5 h-5" />
                Scanner les enseignants
              </>
            )}
          </button>
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertTriangle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Tableau des affectations */}
      {affectationsScannees.length > 0 && (
        <>
          <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                    Matière
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                    Enseignant
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase">
                    Statut
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {affectationsScannees.map((affectation, index) => (
                  <tr key={index} className={affectation.statut === 'manquant' ? 'bg-red-50' : ''}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {affectation.type === 'principal' ? (
                          <Users className="w-4 h-4 text-[#319190]" />
                        ) : (
                          <BookOpen className="w-4 h-4 text-gray-400" />
                        )}
                        <span className="font-medium text-gray-900">
                          {affectation.matiere_nom}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {affectation.enseignant_nom ? (
                        <span className="text-gray-900">
                          {affectation.enseignant_prenom} {affectation.enseignant_nom}
                        </span>
                      ) : (
                        <span className="text-red-600 font-medium">Non affecté</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {affectation.statut === 'affecte' ? (
                        <div className="inline-flex items-center gap-1 rounded-lg bg-green-100 px-2 py-1 text-xs text-green-700">
                          <Check className="w-3 h-3" />
                          Affecté
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-1 rounded-lg bg-red-100 px-2 py-1 text-xs text-red-700">
                          <AlertTriangle className="w-3 h-3" />
                          Manquant
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Résumé */}
          <div className={`
            p-4 rounded-lg border mb-6
            ${affectationsManquantes === 0 
              ? 'bg-green-50 border-green-200' 
              : 'bg-yellow-50 border-yellow-200'
            }
          `}>
            {affectationsManquantes === 0 ? (
              <div className="flex items-center gap-2 text-green-700">
                <Check className="w-5 h-5" />
                <span className="font-medium">
                  Tous les enseignants sont affectés ({affectationsScannees.length} matière(s))
                </span>
              </div>
            ) : (
              <div className="flex items-start gap-2 text-yellow-700">
                <AlertTriangle className="w-5 h-5 mt-0.5" />
                <div>
                  <p className="font-medium">
                    {affectationsManquantes} matière(s) sans enseignant
                  </p>
                  <p className="text-sm mt-1">
                    Vous devez affecter des enseignants avant de continuer
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Boutons d'action */}
          {/*
            ⚠️ PAS DE BOUTON « MODIFIER LES AFFECTATIONS » ICI, ET C'EST VOULU.
            Il y en avait un, qui n'ouvrait rien : son seul effet était
            `alert('Ouverture du module d'affectations - À implémenter')`, une
            boîte du navigateur qui ne menait nulle part. Le module des
            affectations existe pourtant depuis longtemps — le bouton n'avait
            jamais été branché.
            Il n'est pas rebranché pour autant : le message jaune ci-dessus dit
            déjà ce qu'il faut faire, et le personnel sait où affecter un
            enseignant. Un bouton de plus n'apprend rien à personne et oblige à
            quitter l'assistant en cours de route. On affecte ailleurs, puis on
            revient et on clique « Rescanner les affectations », qui, lui, est
            bien branché (`handleScanner`). Décision de Daniel, 05/10/2026.

            Le conteneur lui-même est conditionné par `peutContinuer` : les deux
            boutons étaient MUTUELLEMENT EXCLUSIFS — `peutContinuer` vaut faux
            dès qu'il manque une affectation — donc le garder sans condition
            aurait laissé une rangée vide à l'écran.
          */}
          {peutContinuer && (
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setScanValide(true);
                  onContinuer();
                }}
                className="flex-1 py-3 bg-[#319190] text-white rounded-lg hover:bg-[#2a7a79] transition-colors font-medium"
              >
                Confirmer et continuer
              </button>
            </div>
          )}

          {/* Bouton pour rescanner */}
          {/*
            ⚠️ CE BOUTON N'AVAIT AUCUN RETOUR VISUEL, ET PASSAIT POUR MORT.
            Il appelle pourtant bien `handleScanner`, qui relit les trois sources
            (détails de la classe, affectations matières, enseignant principal).
            Mais quand les affectations n'ont pas changé, il reconstruit un
            tableau IDENTIQUE : rien ne bouge à l'écran, et le clic semble sans
            effet. Il était d'ailleurs le SEUL des deux boutons de scan à ne pas
            montrer l'état `loading` — le premier, lui, affiche « Scan en
            cours... » avec sa roue. Les deux se comportent désormais pareil, et
            l'heure du dernier scan réussi se met à jour à chaque relecture,
            même quand le résultat est le même.
          */}
          <button
            onClick={handleScanner}
            disabled={loading}
            className="w-full mt-3 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                Scan en cours...
              </>
            ) : (
              'Rescanner les affectations'
            )}
          </button>

          {dernierScanLe && !loading && (
            <p className="mt-2 text-center text-xs text-gray-500">
              Dernière relecture à {format(dernierScanLe, 'HH:mm:ss')}
            </p>
          )}
        </>
      )}

      {/* ⚠️ Le retour n'est PLUS conditionne. Il l'etait deux fois - « rien
          n'a ete scanne » et « il manque des affectations » - donc avec une
          classe entierement affectee, c'est-a-dire quand tout va bien, aucun
          des deux n'etait rendu : plus aucun chemin vers le choix de la
          classe, et le retour de l'etape 4 ramenait ici. On tournait entre les
          deux etapes sans pouvoir sortir autrement qu'en fermant la fenetre. */}
      <div className="flex justify-start mt-6">
        <button
          onClick={onRetour}
          className="px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
        >
          Retour
        </button>
      </div>
    </div>
  );
};


// Composant Étape 3 : Emploi du temps avec drag & drop
const EtapeEmploiDuTemps = ({
  classeSelectionnee,
  affectationsScannees,
  activeYear,
  periode,
  creneauxPlaces,
  setCreneauxPlaces,
  setToastInfo,
  onRetour,
  onValider,
  enCours
}) => {
  const [draggedMatiere, setDraggedMatiere] = useState(null);
  const [showHoraireDialog, setShowHoraireDialog] = useState(false);
  const [selectedDrop, setSelectedDrop] = useState(null);
  // Le chemin sans glissement : la matiere choisie au clic, en attente d'un jour.
  const [matiereChoisie, setMatiereChoisie] = useState(null);
  // La fenetre d'horaires sert aussi a MODIFIER : voici le creneau concerne.
  const [creneauEnEdition, setCreneauEnEdition] = useState(null);
  // Le jour vise, qu'on peut desormais changer sans tout refaire.
  const [jourChoisi, setJourChoisi] = useState(null);
  // Le jour survole pendant un glissement : le :hover du navigateur ne se
  // rafraichit pas pendant un glisser natif, donc il ne montrait rien.
  const [jourSurvole, setJourSurvole] = useState(null);
  const [heureDebut, setHeureDebut] = useState('08:00');
  const [heureFin, setHeureFin] = useState('09:00');

  const jours = [
    { id: 1, label: 'Lundi' },
    { id: 2, label: 'Mardi' },
    { id: 3, label: 'Mercredi' },
    { id: 4, label: 'Jeudi' },
    { id: 5, label: 'Vendredi' },
    { id: 6, label: 'Samedi' }
  ];


    // 🆕 NOUVEAUX ÉTATS POUR LA MUTUALISATION
  const [showMutualisationDialog, setShowMutualisationDialog] = useState(false);
  const [creneauToMutualiser, setCreneauToMutualiser] = useState(null);
  const [classesDisponibles, setClassesDisponibles] = useState([]);
  const [classesSelectionnees, setClassesSelectionnees] = useState([]);
  const [loadingMutualisation, setLoadingMutualisation] = useState(false);



  const handleDragStart = (matiere) => {
    setDraggedMatiere(matiere);
    // Un glissement commence : la matiere choisie au clic n'a plus cours.
    setMatiereChoisie(null);
  };

  // Un glissement abandonne - relache hors de toute colonne, ou interrompu
  // par Echap - ne passait par aucun gestionnaire : la matiere restait armee
  // et la surbrillance allumee sur un jour qu'on venait de quitter.
  const handleDragEnd = () => {
    setDraggedMatiere(null);
    setJourSurvole(null);
  };

  // Ouvrir la fenetre d'horaires pour POSER une matiere sur un jour.
  const poserSur = (jour, matiere) => {
    if (!matiere) return;
    setCreneauEnEdition(null);
    setSelectedDrop({ jour, matiere });
    setJourChoisi(jour);
    setShowHoraireDialog(true);
  };

  // ⚠️ `evenement.preventDefault()` est OBLIGATOIRE, et pas pour la forme :
  // la colonne previent l'action par defaut au survol, elle devient donc
  // cible de N'IMPORTE QUEL glissement - y compris un fichier venu du
  // bureau. Sans cela, le navigateur fait ce qu'il fait d'un fichier lache
  // sur une page : il l'ouvre, l'onglet part dessus, et toute la pose est
  // perdue. C'est le geste de quelqu'un qui croit importer son ancien
  // emploi du temps.
  const handleDrop = (evenement, jour) => {
    evenement.preventDefault();
    setJourSurvole(null);

    // Un fichier n'est pas une matiere : on ne fait rien, mais on ne laisse
    // surtout pas le navigateur s'en charger.
    const porteUnFichier = Array.from(evenement.dataTransfer?.types || []).includes('Files');
    if (porteUnFichier || !draggedMatiere) return;

    poserSur(jour, draggedMatiere);
    setDraggedMatiere(null);
  };

  // Le chemin SANS glissement. C'est lui qui regle vraiment la gene : avec
  // une liste de matieres plus haute que la grille, attraper la derniere
  // faisait disparaitre les jours. Choisir puis cliquer ne depend d'aucun
  // ascenseur, et marche au clavier comme sur tablette.
  const handleClicJour = (jour) => {
    if (!matiereChoisie) return;
    poserSur(jour, matiereChoisie);
    setMatiereChoisie(null);
  };

  // La meme fenetre sert a MODIFIER un creneau deja pose : il fallait le
  // supprimer et tout reglisser pour changer une heure.
  const ouvrirModification = (creneau) => {
    setCreneauEnEdition(creneau);
    setSelectedDrop({ jour: creneau.jour, matiere: creneau });
    setJourChoisi(creneau.jour);
    setHeureDebut(creneau.heure_debut);
    setHeureFin(creneau.heure_fin);
    setShowHoraireDialog(true);
  };

  const fermerHoraire = () => {
    setShowHoraireDialog(false);
    setSelectedDrop(null);
    setCreneauEnEdition(null);
    setJourChoisi(null);
  };

  const handleConfirmHoraire = () => {
    if (!selectedDrop) return;

    const jourVise = jourChoisi ?? selectedDrop.jour;
    const enseignantVise = selectedDrop.matiere.enseignant_id;

    if (heureFin <= heureDebut) {
      setToastInfo({
        message: "L'heure de fin doit être postérieure à l'heure de début.",
        type: 'error'
      });
      return;
    }

    // ⚠️ En modification, le creneau est DANS la liste : sans cette exclusion
    // il entre en conflit avec lui-meme et aucune modification n'aboutit
    // jamais. C'est mot pour mot le piege rencontre cote serveur le meme jour.
    const voisins = creneauxPlaces.filter((c) => c.id !== creneauEnEdition?.id);

    const chevauche = (c) => (
      (heureDebut >= c.heure_debut && heureDebut < c.heure_fin)
      || (heureFin > c.heure_debut && heureFin <= c.heure_fin)
      || (heureDebut <= c.heure_debut && heureFin >= c.heure_fin)
    );

    const conflitClasse = voisins.find((c) => c.jour === jourVise && chevauche(c));

    if (conflitClasse) {
      setToastInfo({
        message: `Conflit détecté : ${conflitClasse.matiere_nom} occupe déjà ce créneau`,
        type: 'error'
      });
      return;
    }

    const conflitEnseignant = voisins.find((c) => (
      c.jour === jourVise && c.enseignant_id === enseignantVise && chevauche(c)
    ));

    if (conflitEnseignant) {
      setToastInfo({
        message: `Conflit enseignant : ${selectedDrop.matiere.enseignant_prenom} ${selectedDrop.matiere.enseignant_nom} a déjà un cours à cet horaire`,
        type: 'error'
      });
      return;
    }

    if (creneauEnEdition) {
      // ⚠️ Le cours d'ensemble repose sur l'heure EXACTE : le serveur retrouve
      // les creneaux des autres classes a la seconde pres. Deplacer un creneau
      // mutualise sans defaire sa mise en commun ferait echouer TOUTE la
      // creation au moment de valider, et les creneaux poses partiraient avec.
      // On la defait donc, et on le DIT.
      const aBouge = creneauEnEdition.jour !== jourVise
        || creneauEnEdition.heure_debut !== heureDebut
        || creneauEnEdition.heure_fin !== heureFin;
      const perdLaMutualisation = Boolean(aBouge && creneauEnEdition.est_mutualise);

      setCreneauxPlaces(creneauxPlaces.map((c) => (c.id === creneauEnEdition.id
        ? {
          ...c,
          jour: jourVise,
          heure_debut: heureDebut,
          heure_fin: heureFin,
          ...(perdLaMutualisation
            ? { est_mutualise: false, classes_mutualisees: [], classes_mutualisees_noms: [] }
            : {})
        }
        : c)));

      setToastInfo(perdLaMutualisation
        ? {
          message: "Créneau déplacé. Le cours d'ensemble a été défait : il repose sur "
            + "l'horaire exact, qui vient de changer. Refaites-le si vous le souhaitez.",
          type: 'info',
          title: "Cours d'ensemble défait",
          long: true
        }
        : { message: 'Créneau modifié.', type: 'success' });

      fermerHoraire();
      return;
    }

    const nouveauCreneau = {
      id: Date.now(),
      jour: jourVise,
      heure_debut: heureDebut,
      heure_fin: heureFin,
      matiere_id: selectedDrop.matiere.matiere_id,
      matiere_nom: selectedDrop.matiere.matiere_nom,
      enseignant_id: selectedDrop.matiere.enseignant_id,
      enseignant_nom: selectedDrop.matiere.enseignant_nom,
      enseignant_prenom: selectedDrop.matiere.enseignant_prenom,
      affectation_id: selectedDrop.matiere.affectation_id
    };

    setCreneauxPlaces([...creneauxPlaces, nouveauCreneau]);
    fermerHoraire();

    // Les heures n'etaient JAMAIS reportees : la fenetre se rouvrait sur
    // celles de la pose precedente, et confirmer sur le meme jour annoncait
    // aussitot « Conflit détecté ». Il fallait les retaper a chaque fois.
    //
    // On enchaine donc sur la suite immediate, en gardant la duree qu'on
    // vient de choisir. Si cela sortait de la journee, on ne touche a rien :
    // mieux vaut la valeur precedente qu'une heure impossible.
    const enMinutes = (valeur) => {
      const [h, m] = String(valeur || '').split(':').map(Number);
      return Number.isInteger(h) && Number.isInteger(m) ? h * 60 + m : null;
    };
    const enHeure = (total) => (
      `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
    );

    const debutPose = enMinutes(heureDebut);
    const finPose = enMinutes(heureFin);

    if (debutPose !== null && finPose !== null && finPose > debutPose) {
      const suivanteFin = finPose + (finPose - debutPose);
      if (suivanteFin <= 19 * 60) {
        setHeureDebut(enHeure(finPose));
        setHeureFin(enHeure(suivanteFin));
      }
    }
  };


// Ouvrir le dialog de mutualisation
const handleOpenMutualisation = async (creneau) => {
  console.log('🔗 Ouverture mutualisation pour:', creneau);
  setCreneauToMutualiser(creneau);
  setLoadingMutualisation(true);
  
  try {
    // Appeler l'API pour récupérer les classes disponibles
    const response = await api.timetable.getClassesDisponiblesMutualisation({
      enseignant_id: creneau.enseignant_id,
      jour_semaine: creneau.jour,
      heure_debut: creneau.heure_debut,
      heure_fin: creneau.heure_fin,
      annee_scolaire_id: activeYear.id,
      classe_actuelle_id: classeSelectionnee.id,
      matiere_id: creneau.matiere_id
    });
    
    if (response.success) {
      console.log('✅ Classes disponibles:', response.data);
      setClassesDisponibles(response.data);
      setShowMutualisationDialog(true);
    } else {
      setToastInfo({
        message: 'Aucune classe disponible pour la mutualisation',
        type: 'info'
      });
    }
  } catch (error) {
    console.error('❌ Erreur chargement classes:', error);
    setToastInfo({
      message: 'Erreur lors du chargement des classes disponibles',
      type: 'error'
    });
  } finally {
    setLoadingMutualisation(false);
  }
};

// Confirmer la mutualisation
const handleConfirmMutualisation = () => {
  if (classesSelectionnees.length === 0) {
    setToastInfo({
      message: 'Veuillez sélectionner au moins une classe',
      type: 'error'
    });
    return;
  }

  if (!creneauToMutualiser) return;

  const selectedClasseNames = classesDisponibles
    .filter(classe => classesSelectionnees.some(id => Number(id) === Number(classe.classe_id)))
    .map(classe => classe.classe_nom)
    .filter(Boolean);

  setCreneauxPlaces(prev => prev.map(creneau =>
    creneau.id === creneauToMutualiser.id
      ? {
          ...creneau,
          est_mutualise: true,
          classes_mutualisees: classesSelectionnees,
          classes_mutualisees_noms: selectedClasseNames
        }
      : creneau
  ));

  setToastInfo({
    message: `Cours mutualisé avec ${classesSelectionnees.length} classe(s). La mutualisation sera enregistrée à la validation.`,
    type: 'success'
  });

  setShowMutualisationDialog(false);
  setClassesSelectionnees([]);
  setCreneauToMutualiser(null);
};

// Toggle sélection classe
const toggleClasseSelection = (classeId) => {
  if (classesSelectionnees.includes(classeId)) {
    setClassesSelectionnees(classesSelectionnees.filter(id => id !== classeId));
  } else {
    setClassesSelectionnees([...classesSelectionnees, classeId]);
  }
};


  const supprimerCreneau = (creneauId) => {
    setCreneauxPlaces(creneauxPlaces.filter(c => c.id !== creneauId));
  };

  return (
    // Trois zones : un en-tete, un corps a DEUX ascenseurs independants, un
    // pied. Tout vivait dans l'unique ascenseur de la page : descendre
    // chercher une matiere du bas faisait defiler les en-tetes de jours hors
    // du champ, et on ne savait plus ou l'on deposait.
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-4 shrink-0">
        <h3 className="text-[22px] font-bold text-[#246990]">
          Emploi du temps - {classeSelectionnee.nom}
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          Choisissez une matière puis cliquez un jour - ou glissez-la sur le calendrier.
          Cliquez un créneau posé pour en changer l'horaire ou le jour.
        </p>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-[300px_1fr] gap-6">
        <div className="flex min-h-0 flex-col">
          <h4 className="mb-3 shrink-0 font-medium text-gray-700">Matières à placer</h4>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-2">
          {affectationsScannees
            // Le scan pousse aussi la ligne « Enseignant principal », avec
            // type 'principal', matiere_id a null, et le meme statut 'affecte'
            // que les matieres. Elle etait donc deposable comme les autres, et
            // fabriquait un creneau SANS MATIERE que le serveur ne controle
            // jamais : Number(null) vaut 0 cote base. Le statut dit si
            // l'affectation existe, le type dit ce que c'est - il faut les deux.
            .filter(a => a.type === 'matiere' && a.statut === 'affecte')
            .map((matiere, index) => (
              <button
                type="button"
                key={index}
                draggable
                onDragStart={() => handleDragStart(matiere)}
                onDragEnd={handleDragEnd}
                onClick={() => setMatiereChoisie(matiereChoisie === matiere ? null : matiere)}
                aria-pressed={matiereChoisie === matiere}
                className={`w-full rounded-lg border-2 p-3 text-left transition-all ${matiereChoisie === matiere
                  ? 'border-[#246990] bg-[#246990]/10 ring-2 ring-[#246990]/30'
                  : 'border-[#319190]/30 bg-gradient-to-r from-[#319190]/10 to-[#63b5b4]/10 cursor-move hover:shadow-md'}`}
              >
                <div className="font-medium text-gray-900">{matiere.matiere_nom}</div>
                <div className="mt-1 text-xs text-gray-600">
                  {matiere.enseignant_prenom} {matiere.enseignant_nom}
                </div>
                {matiereChoisie === matiere && (
                  <div className="mt-2 text-xs font-semibold text-[#246990]">
                    Cliquez maintenant le jour voulu
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200">
          {/* Les en-tetes de jours sont HORS du conteneur defilant : ils ne
              peuvent plus quitter le champ, quelle que soit la hauteur de la
              grille ou de la palette. */}
          <div className="grid shrink-0 grid-cols-6 border-b bg-gray-50">
            {/* ⚠️ PAS de `disabled` sur ces en-têtes. `handleDragStart` vide la
                matière choisie dès le départ du glissement : la bande était donc
                désactivée à l'instant précis où son dépôt aurait servi, et viser
                le mot « Lundi » - la cible la plus évidente - ne faisait rien.
                `handleClicJour` sort déjà seul si rien n'est armé. */}
            {jours.map(jour => (
              <button
                type="button"
                key={jour.id}
                onClick={() => handleClicJour(jour.id)}
                onDragOver={(e) => e.preventDefault()}
                onDragEnter={() => setJourSurvole(jour.id)}
                onDrop={(e) => handleDrop(e, jour.id)}
                className={`border-r p-3 text-center font-medium transition-colors last:border-r-0 ${jourSurvole === jour.id
                  ? 'bg-[#246990]/20 text-[#246990]'
                  : matiereChoisie
                    ? 'cursor-pointer bg-[#246990]/10 text-[#246990] hover:bg-[#246990]/20'
                    : 'text-gray-700'}`}
              >
                {jour.label}
              </button>
            ))}
          </div>

          <div className="grid min-h-0 flex-1 grid-cols-6 overflow-y-auto">
            {jours.map(jour => (
              <div
                key={jour.id}
                onDragOver={(e) => e.preventDefault()}
                // ⚠️ `e.target === e.currentTarget` : ces deux événements
                // remontent AUSSI des créneaux déjà posés dans la colonne, et
                // `dragenter` tire sur la nouvelle cible avant `dragleave` sur
                // l'ancienne. La surbrillance s'éteignait donc au-dessus d'un
                // cours en place - c'est-à-dire là où elle sert le plus, puisque
                // c'est là que naissent les conflits d'horaire.
                onDragEnter={(e) => { if (e.target === e.currentTarget) setJourSurvole(jour.id); }}
                onDragLeave={(e) => {
                  if (e.target !== e.currentTarget) return;
                  setJourSurvole((actuel) => (actuel === jour.id ? null : actuel));
                }}
                onDrop={(e) => handleDrop(e, jour.id)}
                onClick={() => handleClicJour(jour.id)}
                className={`min-h-[320px] border-r p-2 transition-colors last:border-r-0 ${jourSurvole === jour.id
                  ? 'bg-[#246990]/10 ring-2 ring-inset ring-[#246990]/40'
                  : matiereChoisie ? 'cursor-pointer bg-[#246990]/5 hover:bg-[#246990]/10' : 'bg-gray-50/50'}`}
              >

             {creneauxPlaces
              .filter(c => c.jour === jour.id)
              .sort((a, b) => a.heure_debut.localeCompare(b.heure_debut))
              .map(creneau => (
                <div
                  key={creneau.id}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); ouvrirModification(creneau); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      ouvrirModification(creneau);
                    }
                  }}
                  title="Modifier l'horaire ou le jour"
                  className="relative group mb-2 cursor-pointer rounded-lg bg-[#319190] p-2 text-xs text-white shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="font-medium">{creneau.matiere_nom}</div>
                  <div className="text-[10px] opacity-90 mt-1">
                    {creneau.heure_debut} - {creneau.heure_fin}
                  </div>
                  
                  {/* 🆕 Badge si mutualisé */}
                  {creneau.est_mutualise && (
                    <div className="mt-1 inline-flex items-center gap-1 rounded-lg bg-white/20 px-1 text-[9px]">
                      <Link2 className="h-2.5 w-2.5" />
                      Cours d'ensemble
                    </div>
                  )}
                  
                  {/* Boutons d'action */}
                  <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {/* Le crayon. La carte entiere est cliquable, mais rien ne le
                        disait : un geste que personne ne voit n'existe pas. */}
                    <button
                      onClick={(e) => { e.stopPropagation(); ouvrirModification(creneau); }}
                      className="flex h-5 w-5 items-center justify-center rounded-lg bg-white/25 transition-colors hover:bg-white/45"
                      title="Modifier l'horaire ou le jour"
                      aria-label="Modifier l'horaire ou le jour"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>

                    {/* 🆕 Bouton mutualisation */}
                    {!creneau.est_mutualise && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleOpenMutualisation(creneau); }}
                        className="flex h-5 w-5 items-center justify-center rounded-lg bg-blue-500 transition-colors hover:bg-blue-600"
                        title="Cours d'ensemble"
                      >
                        <Link2 className="h-3 w-3" />
                      </button>
                    )}
                    
                    {/* Bouton supprimer */}
                    <button
                      onClick={(e) => { e.stopPropagation(); supprimerCreneau(creneau.id); }}
                      className="flex h-5 w-5 items-center justify-center rounded-lg bg-red-500 transition-colors hover:bg-red-600"
                      title="Supprimer"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              ))}

              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-4 flex shrink-0 items-center justify-between gap-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
        <p className="text-sm text-blue-900">
          <strong>{creneauxPlaces.length}</strong>
          {' '}créneau{creneauxPlaces.length > 1 ? 'x' : ''} placé{creneauxPlaces.length > 1 ? 's' : ''}
        </p>
        {matiereChoisie && (
          <p className="text-sm font-semibold text-[#246990]">
            {matiereChoisie.matiere_nom} en attente d'un jour
          </p>
        )}
      </div>

      <div className="mt-4 flex shrink-0 justify-between">
        <button
          onClick={onRetour}
          className="px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
        >
          Retour
        </button>
        <button
          onClick={onValider}
          disabled={creneauxPlaces.length === 0 || enCours}
          className={`
            px-6 py-3 rounded-lg font-medium transition-all
            ${creneauxPlaces.length > 0 && !enCours
              ? 'bg-[#246990] text-white hover:bg-[#1f5a7a]'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }
          `}
        >
          {/* Le bouton annonce ce qu'il va faire. « Valider l'emploi du temps »
              ne disait ni combien de creneaux, ni sur quelle periode — on
              decouvrait apres. */}
          {/* L'etat de chargement existait et n'etait montre nulle part : on
              cliquait, rien ne bougeait, et on pouvait recliquer - sur une
              creation qui ecrit plusieurs centaines de creneaux. */}
          {enCours
            ? 'Création en cours...'
            : (creneauxPlaces.length > 0 && periode?.semaines
              ? `Créer ${creneauxPlaces.length * periode.semaines} créneaux sur ${periode.semaines} semaines`
              : "Valider l'emploi du temps")}
        </button>
      </div>

      <Dialog open={showHoraireDialog} onOpenChange={(ouvert) => { if (!ouvert) fermerHoraire(); }}>
        <DialogContent
          className="w-[95vw] max-w-lg overflow-hidden p-0 gap-0"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader className="space-y-0 border-b px-5 py-4 text-left">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              {creneauEnEdition ? 'Modifier le créneau' : 'Définir les horaires'}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              {selectedDrop?.matiere.matiere_nom}
            </DialogDescription>
          </DialogHeader>

          <div className="px-5 py-4">

            {/* Le jour se change ici : sans cela, se tromper de colonne
                obligeait a supprimer le créneau et à tout refaire. */}
            <div className="mb-4">
              <label className="text-sm font-medium text-gray-700" htmlFor="jour-du-creneau">Jour</label>
              <div className="mt-1">
                <ChampSelect
                  id="jour-du-creneau"
                  valeur={jourChoisi ?? selectedDrop?.jour}
                  onChange={(valeur) => setJourChoisi(Number(valeur))}
                  options={jours.map((jour) => ({ valeur: jour.id, libelle: jour.label }))}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Heure début</label>

                <input
                type="time"
                value={heureDebut}
                onChange={(e) => setHeureDebut(e.target.value)}
                min="07:00"
                max="19:00"
                step="1800" // pas de 30 minutes
                className="w-full mt-1 border-2 border-gray-200 rounded-lg p-2 focus:border-[#319190] focus:ring-[#319190]"
              />

              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Heure fin</label>
               <input
                  type="time"
                  value={heureFin}
                  onChange={(e) => setHeureFin(e.target.value)}
                  min="07:00"
                  max="19:00"
                  step="1800"
                  className="w-full mt-1 border-2 border-gray-200 rounded-lg p-2 focus:border-[#319190] focus:ring-[#319190]"
                />

              </div>
            </div>

            {/* Un cours d'ensemble repose sur l'horaire EXACT : le déplacer
                le défait. On le dit AVANT, pas après. */}
            {creneauEnEdition?.est_mutualise && (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Ce créneau est un cours d'ensemble. En changer le jour ou l'horaire le défera :
                il repose sur l'horaire exact des autres classes.
              </div>
            )}

          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4">
            <Button variant="outline" className={BOUTON_PIED} onClick={fermerHoraire}>
              Annuler
            </Button>
            <Button
              onClick={handleConfirmHoraire}
              className={`${BOUTON_PIED} border border-transparent bg-[#246990] text-white hover:bg-[#1f5a7a]`}
            >
              {creneauEnEdition ? 'Enregistrer' : 'Confirmer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


     {/* 🆕 DIALOG DE MUTUALISATION - VERSION AMÉLIORÉE */}
      <Dialog
        open={showMutualisationDialog}
        onOpenChange={(ouvert) => {
          if (!ouvert) {
            setShowMutualisationDialog(false);
            setClassesSelectionnees([]);
          }
        }}
      >
        <DialogContent
          className="w-[95vw] max-w-lg overflow-hidden p-0 gap-0 flex flex-col !max-h-[85vh]"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader className="space-y-0 border-b px-5 py-4 text-left">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Cours d'ensemble
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Le même cours, donné à plusieurs classes en même temps.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            
            {/* ✅ Texte amélioré */}
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-900">
                {creneauToMutualiser?.matiere_nom} - {creneauToMutualiser?.enseignant_prenom} {creneauToMutualiser?.enseignant_nom}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {jours.find((j) => j.id === creneauToMutualiser?.jour)?.label || 'Jour inconnu'}
                {' '}de {creneauToMutualiser?.heure_debut} à {creneauToMutualiser?.heure_fin}
              </p>
            </div>

            {loadingMutualisation ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#319190] mx-auto"></div>
                <p className="text-sm text-gray-600 mt-2">Chargement...</p>
              </div>
            ) : classesDisponibles.length === 0 ? (
              <div className="text-center py-8 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm font-medium text-yellow-800">Aucune classe disponible</p>
                <p className="text-xs text-yellow-700 mt-2">
                  Aucune autre classe n'a ce même enseignant le même jour à la même heure.
                </p>
              </div>
            ) : (
              <>
                {/* ✅ Message explicatif */}
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-800 flex items-start gap-2">
                    <span className="text-lg">ℹ️</span>
                    <span>
                      Ces classes ont déjà ce même enseignant programmé le même jour à la même heure. 
                      Sélectionnez les classes à mutualiser pour créer un <strong>cours d'ensemble</strong>.
                    </span>
                  </p>
                </div>

                <p className="text-sm font-medium text-gray-700 mb-3">
                  Sélectionnez les classes à mutualiser :
                </p>
                
                <div className="space-y-2 mb-4">
                  {classesDisponibles.map((classe) => (
                    <label
                      key={classe.classe_id}
                      className={`
                        flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all
                        ${classesSelectionnees.includes(classe.classe_id)
                          ? 'border-[#319190] bg-[#319190]/10'
                          : 'border-gray-200 hover:border-[#319190]/30'
                        }
                      `}
                    >
                      <input
                        type="checkbox"
                        checked={classesSelectionnees.includes(classe.classe_id)}
                        onChange={() => toggleClasseSelection(classe.classe_id)}
                        className="w-4 h-4 text-[#319190] rounded focus:ring-[#319190]"
                      />
                      <span className="ml-3 font-medium text-gray-900">
                        {classe.classe_nom}
                      </span>
                      {classe.est_cours_mutualise && (
                        <span className="ml-auto text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                          Déjà mutualisé
                        </span>
                      )}
                    </label>
                  ))}
                </div>

                {classesSelectionnees.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                    <p className="text-xs text-green-800">
                      {classesSelectionnees.length} classe{classesSelectionnees.length > 1 ? 's' : ''}
                  {' '}sélectionnée{classesSelectionnees.length > 1 ? 's' : ''} pour le cours d'ensemble
                    </p>
                  </div>
                )}
              </>
            )}

          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4">
            <Button
              variant="outline"
              className={BOUTON_PIED}
              disabled={loadingMutualisation}
              onClick={() => {
                setShowMutualisationDialog(false);
                setClassesSelectionnees([]);
              }}
            >
              Annuler
            </Button>
            {classesDisponibles.length > 0 && (
              <Button
                onClick={handleConfirmMutualisation}
                disabled={classesSelectionnees.length === 0 || loadingMutualisation}
                className={`${BOUTON_PIED} border border-transparent bg-[#246990] text-white hover:bg-[#1f5a7a]`}
              >
                {loadingMutualisation ? 'Création...' : 'Confirmer'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};


export default EmploiDuTempsAnnuelModal;